/**
 * Pure scoring math for prompt coverage. No runtime dependencies on the
 * database or any server-only modules — safe to import from client code.
 *
 * The server (`services/prompt-coverage.ts`) uses these for the authoritative
 * `computeCoverage` call. The client (project create form) uses them to
 * simulate selection-based coverage without round-tripping to the server on
 * every toggle.
 */

import type {
  CoverageCell,
  CoverageDecomposition,
  CoverageDimension,
  CoverageGap,
  CoverageReport,
  CoverageStageRow,
  FunnelStage,
} from "../types/prompt-coverage";

export type { CoverageDecomposition } from "../types/prompt-coverage";
import type {
  DiscoveryQueryType,
  PromptGeneratorFramework,
} from "../types/discovery-query";

// ---------------------------------------------------------------------------
// Constants — single source of truth for scoring weights and targets
// ---------------------------------------------------------------------------

// Weight by buyer purchase intent: decision is closest to purchase and
// weighted highest for commercial value.
export const STAGE_WEIGHTS: Record<FunnelStage, number> = {
  awareness: 1,
  consideration: 2,
  decision: 3,
};

export const STAGE_TARGETS: Record<FunnelStage, number> = {
  awareness: 2,
  consideration: 2,
  decision: 1,
};

// Composite weights — matrix breadth dominates because an empty matrix is
// the clearest signal of weak coverage. Stage/framework sub-scores saturate
// quickly once enough prompts exist and shouldn't mask an empty matrix.
export const SCORE_WEIGHT_STAGE = 0.25;
export const SCORE_WEIGHT_DIMENSION = 0.6;
export const SCORE_WEIGHT_FRAMEWORK = 0.15;

export const FRAMEWORK_TARGET_COUNT = 2;

// Best-effort mapping from queryType to a buyer-journey stage.
const QUERY_TYPE_TO_STAGE: Record<DiscoveryQueryType, FunnelStage> = {
  category_best: "consideration",
  use_case_solution: "awareness",
  budget_constrained: "decision",
  solution_comparison: "consideration",
  segment_focused: "consideration",
  local_services: "consideration",
  local_comparison: "consideration",
  local_reviews: "decision",
  category_entry_point: "awareness",
  jobs_to_be_done: "awareness",
  buyers_journey_awareness: "awareness",
  buyers_journey_consideration: "consideration",
  buyers_journey_decision: "decision",
  stakeholder_perspective: "consideration",
  brand_identity: "awareness",
  brand_comparison: "decision",
};

const STAGE_ORDER: Record<FunnelStage, number> = {
  awareness: 0,
  consideration: 1,
  decision: 2,
};

const STAGES: FunnelStage[] = ["awareness", "consideration", "decision"];

/** Display labels for the three stages — shared so the prompt manager, the
 *  prompt editor and the coverage matrix can't drift apart. */
export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  awareness: "Awareness",
  consideration: "Consideration",
  decision: "Decision",
};

/**
 * The stage a prompt's answers are counted in. Note the fallback: `queryType`
 * is an unconstrained `String` in Postgres (the enum lives only in Zod), so
 * anything unrecognized lands in consideration and therefore inside the
 * headline Share of Voice. Worth auditing a property's distinct query types
 * before trusting a stage split.
 */
export function funnelStageFor(queryType: DiscoveryQueryType): FunnelStage {
  return QUERY_TYPE_TO_STAGE[queryType] ?? "consideration";
}

export function stageDistance(a: FunnelStage, b: FunnelStage): number {
  return Math.abs(STAGE_ORDER[a] - STAGE_ORDER[b]);
}

// ---------------------------------------------------------------------------
// Composite score from aggregated inputs — used by both server and client
// ---------------------------------------------------------------------------

export interface ScoreInput {
  stageCounts: Record<FunnelStage, number>;
  cells: CoverageCell[];
  dimensions: CoverageDimension[];
  frameworksPresent: PromptGeneratorFramework[];
}

export interface ScoreOutput {
  score: number;
  subScores: {
    stage: { value: number; weight: number };
    dimension: { value: number; weight: number };
    framework: { value: number; weight: number };
  };
  stageRows: CoverageStageRow[];
  frameworkScore: number;
}

export function computeCompositeScore(input: ScoreInput): ScoreOutput {
  const stageRows: CoverageStageRow[] = STAGES.map((s) => ({
    key: s,
    label: s[0].toUpperCase() + s.slice(1),
    weight: STAGE_WEIGHTS[s],
    target: STAGE_TARGETS[s],
    actualCount: input.stageCounts[s] ?? 0,
  }));

  const totalStageWeight = STAGES.reduce((sum, s) => sum + STAGE_WEIGHTS[s], 0);
  const stageScore =
    stageRows.reduce((sum, row) => {
      const ratio = row.target === 0 ? 1 : Math.min(row.actualCount / row.target, 1);
      return sum + ratio * row.weight;
    }, 0) / totalStageWeight;

  const frameworkScore = Math.min(
    input.frameworksPresent.length / FRAMEWORK_TARGET_COUNT,
    1,
  );

  const dimensionScore = input.dimensions.length
    ? input.cells.reduce((sum, c) => sum + (c.promptCount > 0 ? 1 : 0), 0) /
      (input.dimensions.length * STAGES.length)
    : 1;

  const score = Math.round(
    (stageScore * SCORE_WEIGHT_STAGE +
      dimensionScore * SCORE_WEIGHT_DIMENSION +
      frameworkScore * SCORE_WEIGHT_FRAMEWORK) *
      100,
  );

  return {
    score,
    subScores: {
      stage: { value: stageScore, weight: SCORE_WEIGHT_STAGE },
      dimension: { value: dimensionScore, weight: SCORE_WEIGHT_DIMENSION },
      framework: { value: frameworkScore, weight: SCORE_WEIGHT_FRAMEWORK },
    },
    stageRows,
    frameworkScore,
  };
}

export function buildGaps(
  cells: CoverageCell[],
  dimensions: CoverageDimension[],
  stageRows: CoverageStageRow[],
): CoverageGap[] {
  const gaps: CoverageGap[] = [];
  for (const cell of cells) {
    if (cell.promptCount > 0) continue;
    const weight = STAGE_WEIGHTS[cell.stage];
    const priority: "high" | "medium" | "low" =
      weight === 3 ? "high" : weight === 2 ? "medium" : "low";
    const dim = dimensions.find((d) => d.key === cell.dimensionKey);
    gaps.push({
      stage: cell.stage,
      dimensionKey: cell.dimensionKey,
      priority,
      recommendation: dim
        ? `Add a ${cell.stage}-stage prompt targeting "${dim.label}"`
        : `Add a ${cell.stage}-stage prompt`,
    });
  }
  for (const row of stageRows) {
    if (row.actualCount >= row.target) continue;
    const priority: "high" | "medium" | "low" =
      row.weight === 3 ? "high" : row.weight === 2 ? "medium" : "low";
    gaps.push({
      stage: row.key,
      dimensionKey: null,
      priority,
      recommendation: `Add ${row.target - row.actualCount} more ${row.key}-stage prompt(s) (currently ${row.actualCount}/${row.target})`,
    });
  }
  gaps.sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.priority] - order[b.priority];
  });
  return gaps;
}

// ---------------------------------------------------------------------------
// Simulation: compute a CoverageReport for a subset of prompts using a
// pre-computed decomposition. Runs client-side for instant live updates.
// ---------------------------------------------------------------------------

export function simulateCoverage(
  decomposition: CoverageDecomposition,
  selectedIds: string[],
): CoverageReport {
  const stageCounts: Record<FunnelStage, number> = {
    awareness: 0,
    consideration: 0,
    decision: 0,
  };

  // cellMap key = `${stage}:${dimensionKey}`
  const cellMap = new Map<string, CoverageCell>();
  for (const stage of STAGES) {
    for (const dim of decomposition.dimensions) {
      cellMap.set(`${stage}:${dim.key}`, {
        stage,
        dimensionKey: dim.key,
        promptCount: 0,
        promptIds: [],
      });
    }
  }

  const frameworks = new Set<PromptGeneratorFramework>();

  for (const id of selectedIds) {
    const assignment = decomposition.assignments[id];
    if (!assignment) continue;
    stageCounts[assignment.stage]++;
    if (assignment.framework) frameworks.add(assignment.framework);
    for (const dimKey of assignment.coveredDimensionKeys) {
      const cell = cellMap.get(`${assignment.stage}:${dimKey}`);
      if (cell) {
        cell.promptCount++;
        cell.promptIds.push(id);
      }
    }
  }

  const cells = Array.from(cellMap.values());
  const frameworksPresent = Array.from(frameworks);
  const { score, subScores, stageRows, frameworkScore } = computeCompositeScore({
    stageCounts,
    cells,
    dimensions: decomposition.dimensions,
    frameworksPresent,
  });
  const gaps = buildGaps(cells, decomposition.dimensions, stageRows);

  return {
    propertyId: decomposition.propertyId,
    score,
    subScores,
    totalPrompts: Object.keys(decomposition.assignments).length,
    activePromptsWithEmbeddings: selectedIds.filter(
      (id) => decomposition.assignments[id],
    ).length,
    stages: stageRows,
    dimensions: decomposition.dimensions,
    cells,
    frameworks: {
      present: frameworksPresent,
      targetCount: FRAMEWORK_TARGET_COUNT,
      score: frameworkScore,
    },
    gaps,
    warnings: decomposition.warnings,
  };
}

// ---------------------------------------------------------------------------
// Greedy max-coverage selection
// ---------------------------------------------------------------------------

export interface MaximizeCoverageResult {
  selectedIds: string[];
  report: CoverageReport;
}

/**
 * Greedy set-cover style selection: at each step, pick the prompt whose
 * addition produces the largest gain in composite score. Stop at `maxCount`
 * or when no prompt can improve the score.
 *
 * Greedy is provably within (1 - 1/e) ≈ 63% of optimal for submodular set
 * cover; for this scoring function it typically produces near-optimal
 * selections because most gain comes from filling new matrix cells.
 */
export function maximizeCoverage(
  decomposition: CoverageDecomposition,
  maxCount: number,
): MaximizeCoverageResult {
  const available = Object.keys(decomposition.assignments);
  const cap = Math.max(0, Math.min(maxCount, available.length));

  const selected: string[] = [];
  let currentScore = 0;

  while (selected.length < cap) {
    let best: { id: string; score: number } | null = null;
    const remaining = available.filter((id) => !selected.includes(id));
    for (const id of remaining) {
      const trial = simulateCoverage(decomposition, [...selected, id]);
      if (!best || trial.score > best.score) {
        best = { id, score: trial.score };
      }
    }
    if (!best || best.score <= currentScore) break; // no improvement
    selected.push(best.id);
    currentScore = best.score;
  }

  return {
    selectedIds: selected,
    report: simulateCoverage(decomposition, selected),
  };
}
